import React, { FC } from 'react';
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import {
  Block,
  BLOCKS,
  Document,
  Inline,
  INLINES,
  MARKS,
} from '@contentful/rich-text-types';
import has from 'lodash/has';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import { EmbeddedAsset } from './EmbeddedAsset';
import { HyperlinkRichText } from './hyperlink';
import { EntryHyperlink } from './entryHyperlink';
import classNames from 'classnames';

interface RichTextProps {
  content: Document | Block | Inline;
  addParagraphMargins?: boolean;
  openLinkSameWindow?: boolean;
  removeHoverEffect?: boolean;
  classes?: string;
}

export const isRichText = (x: Document | unknown): x is Document => {
  return ['data', 'content', 'nodeType'].every((prop) => has(x, prop));
};

const PlainHyperlink = (props: any) => (
  <HyperlinkRichText {...props} type="PlainLink" />
);
const AssetHyperlink = (props: any) => (
  <HyperlinkRichText {...props} type="AssetLink" />
);

export const renderDocument = (
  content: any,
  addParagraphMargins: boolean,
  openLinkSameWindow: boolean,
  removeHoverEffect?: boolean,
  classes = '',
) => {
  if (!isRichText(content)) return null;
  const removeHoverClass = removeHoverEffect ? 'text-charcoal-v2' : '';
  const options: Options = {
    renderMark: {
      [MARKS.BOLD]: (text) => <span className="font-bold">{text}</span>,
      [MARKS.ITALIC]: (text) => <span className="italic">{text}</span>,
    },
    renderNode: {
      [INLINES.HYPERLINK]: (props) => {
        return PlainHyperlink({ ...props, openLinkSameWindow });
      },
      [INLINES.ASSET_HYPERLINK]: AssetHyperlink,
      [INLINES.ENTRY_HYPERLINK]: EntryHyperlink,
      [BLOCKS.EMBEDDED_ASSET]: EmbeddedAsset,
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <p
          className={classNames('leading-6 lg:leading-7 last:mb-0', {
            [classes]: !!classes,
            [removeHoverClass]: !!removeHoverClass,
            'mb-6 md:mb-8': addParagraphMargins,
          })}
        >
          {children}
        </p>
      ),
      [BLOCKS.HEADING_1]: (node, children) => (
        <HeaderText
          classes={`mb-4 md:mb-6 ${removeHoverClass}`}
          size={HeaderSize.H1}
          text={children}
        />
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <HeaderText
          classes={`mb-4 md:mb-6 ${removeHoverClass}`}
          size={HeaderSize.H2}
          text={children}
        />
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <HeaderText
          classes={`mb-4 md:mb-6 ${removeHoverClass}`}
          size={HeaderSize.H3}
          text={children}
        />
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <HeaderText
          classes={`mb-4 md:mb-6 ${removeHoverClass}`}
          size={HeaderSize.H4}
          text={children}
        />
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul
          className={classNames('ml-0 list-disc pl-6 text-left ', {
            'mb-4 md:mb-6': addParagraphMargins,
          })}
        >
          {children}
        </ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className="mb-4 ml-0 list-decimal space-y-2 pl-6 text-left md:mb-6">
          {children}
        </ol>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    },
    // Allows for shift + enter breaks
    renderText: (text) => {
      return text
        .split('\n')
        .reduce<React.ReactNode[]>((children, textSegment, index) => {
          const elementsToAdd: React.ReactNode[] = [];
          if (index > 0) {
            elementsToAdd.push(<br key={index} />);
          }
          elementsToAdd.push(textSegment);
          return [...children, ...elementsToAdd];
        }, []);
    },
  };

  const renderRichText = documentToReactComponents(content, options);

  return renderRichText ? renderRichText : null;
};

const RichText: FC<RichTextProps> = ({
  content,
  addParagraphMargins = true,
  openLinkSameWindow = false,
  removeHoverEffect,
  classes = '',
}) =>
  renderDocument(
    content,
    addParagraphMargins,
    openLinkSameWindow,
    removeHoverEffect,
    classes,
  );

export default React.memo(RichText);
